import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './OwnerPortalPage.css';

const emptyProfile = {
  full_name: '',
  email: '',
  phone: '',
  address: '',
  preferred_language: 'hu',
  bank_account_iban: '',
  payout_currency: 'HUF',
  emergency_contact: ''
};

const emptyAircraft = {
  registration: '',
  aircraft_type: '',
  base_airport: '',
  passenger_seats: '',
  hourly_operating_cost: '',
  fuel_type: '',
  insurance_provider: '',
  insurance_expiry_date: '',
  public_description: ''
};

const statusLabel = (value, t) => {
  const key = String(value || 'pending').toLowerCase();
  if (key === 'approved') return t('owner.statusApproved');
  if (key === 'rejected') return t('owner.statusRejected');
  return t('owner.statusPending');
};

const statusClass = (value) => `owner-status owner-status-${String(value || 'pending').toLowerCase()}`;

const OwnerPortalNav = ({ t }) => (
  <nav className="owner-tabs" aria-label={t('owner.navLabel')}>
    <Link to="/owner/dashboard">{t('owner.navDashboard')}</Link>
    <Link to="/owner/profile">{t('owner.navProfile')}</Link>
    <Link to="/owner/aircraft">{t('owner.navAircraft')}</Link>
    <Link to="/safety-forum">{t('owner.navSafetyForum')}</Link>
  </nav>
);

const OwnerPortalPage = ({ view = 'dashboard' }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(emptyProfile);
  const [aircraft, setAircraft] = useState(emptyAircraft);
  const [documentType, setDocumentType] = useState('insurance');
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadOwner = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get('/api/owners/me');
      setData(response.data);
      setProfile({ ...emptyProfile, ...(response.data.profile || {}) });
      setAircraft({ ...emptyAircraft, ...(response.data.aircraft || {}) });
    } catch (err) {
      setError(err.response?.data?.error || t('owner.errLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadOwner();
  }, [loadOwner]);

  const missingDocuments = data?.dashboard?.missingDocuments;
  const completion = data?.dashboard?.profileCompletionPercent || 0;
  const contractDownloadAvailable =
    String(data?.dashboard?.ownerStatus || '').toLowerCase() === 'approved' &&
    String(data?.dashboard?.aircraftStatus || '').toLowerCase() === 'approved';

  const profileRequiredFilled = useMemo(() => (
    ['full_name', 'email', 'phone', 'address', 'preferred_language', 'bank_account_iban', 'payout_currency']
      .every((field) => String(profile[field] || '').trim())
  ), [profile]);

  const aircraftRequiredFilled = useMemo(() => (
    ['registration', 'aircraft_type', 'base_airport', 'passenger_seats', 'hourly_operating_cost', 'fuel_type', 'insurance_provider', 'insurance_expiry_date']
      .every((field) => String(aircraft[field] || '').trim())
  ), [aircraft]);

  const saveProfile = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const response = await axios.put('/api/owners/profile', profile);
      setProfile({ ...emptyProfile, ...(response.data.profile || {}) });
      setMessage(t('owner.profileSaved'));
      await loadOwner();
    } catch (err) {
      setError(err.response?.data?.error || t('owner.profileSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const saveAircraft = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const response = await axios.put('/api/owners/aircraft', aircraft);
      setAircraft({ ...emptyAircraft, ...(response.data.aircraft || {}) });
      setMessage(t('owner.aircraftSaved'));
      await loadOwner();
    } catch (err) {
      setError(err.response?.data?.error || t('owner.aircraftSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const uploadDocuments = async (event) => {
    event.preventDefault();
    if (!files || files.length === 0) return;
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const formData = new FormData();
      formData.append('document_type', documentType);
      Array.from(files).forEach((file) => formData.append('files', file));
      await axios.post('/api/owners/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFiles(null);
      event.target.reset();
      setMessage(t('owner.filesUploaded'));
      await loadOwner();
    } catch (err) {
      setError(err.response?.data?.error || t('owner.filesUploadError'));
    } finally {
      setSaving(false);
    }
  };

  const downloadContract = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      const response = await axios.get('/api/owners/contract.pdf', { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      const registration = String(data?.aircraft?.registration || aircraft.registration || 'aircraft')
        .trim()
        .replace(/[^a-z0-9_-]+/gi, '-')
        .replace(/^-+|-+$/g, '') || 'aircraft';
      link.href = blobUrl;
      link.download = `oscair-partner-szerzodes-${registration}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError(
        err.response?.data && typeof err.response.data === 'object' && !(err.response.data instanceof Blob)
          ? err.response.data.error || t('owner.contractDownloadError')
          : t('owner.contractDownloadError')
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="owner-page"><p className="owner-loading">{t('owner.loading')}</p></div>;
  }

  return (
    <div className="owner-page">
      <header className="owner-hero">
        <p className="owner-eyebrow">{t('owner.eyebrow')}</p>
        <h1>{t('owner.title')}</h1>
        <p>{t('owner.intro')}</p>
      </header>

      <OwnerPortalNav t={t} />

      {error && <div className="owner-alert owner-alert-error" role="alert">{error}</div>}
      {message && <div className="owner-alert owner-alert-success" role="status">{message}</div>}

      {view === 'dashboard' && (
        <section className="owner-grid owner-dashboard-grid">
          <article className="owner-card">
            <span className="owner-card-label">{t('owner.ownerAccount')}</span>
            <strong className={statusClass(data?.dashboard?.ownerStatus)}>{statusLabel(data?.dashboard?.ownerStatus, t)}</strong>
          </article>
          <article className="owner-card">
            <span className="owner-card-label">{t('owner.aircraftApproval')}</span>
            <strong className={statusClass(data?.dashboard?.aircraftStatus)}>{statusLabel(data?.dashboard?.aircraftStatus, t)}</strong>
          </article>
          <article className="owner-card">
            <span className="owner-card-label">{t('owner.documents')}</span>
            <strong className={missingDocuments ? 'owner-warning-text' : 'owner-ok-text'}>
              {missingDocuments ? t('owner.documentsMissing') : t('owner.documentsUploaded')}
            </strong>
          </article>
          <article className="owner-card">
            <span className="owner-card-label">{t('owner.profileCompletion')}</span>
            <strong>{completion}%</strong>
            <div className="owner-progress"><span style={{ width: `${completion}%` }} /></div>
          </article>

          <article className="owner-card owner-card-wide">
            <h2>{t('owner.quickActions')}</h2>
            <div className="owner-actions">
              <Link className="btn btn-primary" to="/owner/profile">{t('owner.editProfile')}</Link>
              <Link className="btn btn-secondary" to="/owner/aircraft">{t('owner.editAircraft')}</Link>
              {contractDownloadAvailable && (
                <button className="btn btn-secondary" type="button" onClick={downloadContract} disabled={saving}>
                  {t('owner.downloadContract')}
                </button>
              )}
              <Link className="btn btn-secondary" to="/booking/request-flight">{t('owner.publishFlight')}</Link>
              <Link className="btn btn-secondary" to="/safety-forum">{t('owner.openSafetyForum')}</Link>
            </div>
          </article>
        </section>
      )}

      {view === 'profile' && (
        <section className="owner-panel">
          <div className="owner-section-heading">
            <h2>{t('owner.profileTitle')}</h2>
            <span className={profileRequiredFilled ? 'owner-ok-text' : 'owner-warning-text'}>
              {profileRequiredFilled ? t('owner.requiredComplete') : t('owner.requiredMissing')}
            </span>
          </div>
          <form className="owner-form owner-form-grid" onSubmit={saveProfile}>
            <label>
              <span>{t('owner.fullName')}</span>
              <input value={profile.full_name || ''} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} required />
            </label>
            <label>
              <span>{t('owner.email')}</span>
              <input type="email" value={profile.email || ''} onChange={(e) => setProfile({ ...profile, email: e.target.value })} required />
            </label>
            <label>
              <span>{t('owner.phone')}</span>
              <input value={profile.phone || ''} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} required />
            </label>
            <label>
              <span>{t('owner.preferredLanguage')}</span>
              <select value={profile.preferred_language || 'hu'} onChange={(e) => setProfile({ ...profile, preferred_language: e.target.value })} required>
                <option value="hu">{t('languages.hu')}</option>
                <option value="en">{t('languages.en')}</option>
                <option value="he">{t('languages.he')}</option>
              </select>
            </label>
            <label className="owner-full">
              <span>{t('owner.address')}</span>
              <input value={profile.address || ''} onChange={(e) => setProfile({ ...profile, address: e.target.value })} required />
            </label>
            <label>
              <span>{t('owner.bankAccount')}</span>
              <input value={profile.bank_account_iban || ''} onChange={(e) => setProfile({ ...profile, bank_account_iban: e.target.value })} required />
            </label>
            <label>
              <span>{t('owner.payoutCurrency')}</span>
              <select value={profile.payout_currency || 'HUF'} onChange={(e) => setProfile({ ...profile, payout_currency: e.target.value })} required>
                <option value="HUF">HUF</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </label>
            <label className="owner-full">
              <span>{t('owner.emergencyContact')}</span>
              <input value={profile.emergency_contact || ''} onChange={(e) => setProfile({ ...profile, emergency_contact: e.target.value })} />
            </label>
            <button className="btn btn-primary" type="submit" disabled={saving}>{t('owner.saveProfile')}</button>
          </form>
        </section>
      )}

      {view === 'aircraft' && (
        <section className="owner-panel">
          <div className="owner-section-heading">
            <h2>{t('owner.aircraftTitle')}</h2>
            <span className={aircraftRequiredFilled ? 'owner-ok-text' : 'owner-warning-text'}>
              {aircraftRequiredFilled ? t('owner.requiredComplete') : t('owner.requiredMissing')}
            </span>
          </div>
          <form className="owner-form owner-form-grid" onSubmit={saveAircraft}>
            <label>
              <span>{t('owner.registration')}</span>
              <input value={aircraft.registration || ''} onChange={(e) => setAircraft({ ...aircraft, registration: e.target.value })} required />
            </label>
            <label>
              <span>{t('owner.aircraftType')}</span>
              <input value={aircraft.aircraft_type || ''} onChange={(e) => setAircraft({ ...aircraft, aircraft_type: e.target.value })} required />
            </label>
            <label>
              <span>{t('owner.baseAirport')}</span>
              <input value={aircraft.base_airport || ''} onChange={(e) => setAircraft({ ...aircraft, base_airport: e.target.value })} required />
            </label>
            <label>
              <span>{t('owner.passengerSeats')}</span>
              <input type="number" min="0" value={aircraft.passenger_seats || ''} onChange={(e) => setAircraft({ ...aircraft, passenger_seats: e.target.value })} required />
            </label>
            <label>
              <span>{t('owner.hourlyCost')}</span>
              <input type="number" min="0" step="1" value={aircraft.hourly_operating_cost || ''} onChange={(e) => setAircraft({ ...aircraft, hourly_operating_cost: e.target.value })} required />
            </label>
            <label>
              <span>{t('owner.fuelType')}</span>
              <input value={aircraft.fuel_type || ''} onChange={(e) => setAircraft({ ...aircraft, fuel_type: e.target.value })} required />
            </label>
            <label>
              <span>{t('owner.insuranceProvider')}</span>
              <input value={aircraft.insurance_provider || ''} onChange={(e) => setAircraft({ ...aircraft, insurance_provider: e.target.value })} required />
            </label>
            <label>
              <span>{t('owner.insuranceExpiry')}</span>
              <input type="date" value={aircraft.insurance_expiry_date || ''} onChange={(e) => setAircraft({ ...aircraft, insurance_expiry_date: e.target.value })} required />
            </label>
            <label className="owner-full">
              <span>{t('owner.publicDescription')}</span>
              <textarea rows="5" value={aircraft.public_description || ''} onChange={(e) => setAircraft({ ...aircraft, public_description: e.target.value })} />
            </label>
            <button className="btn btn-primary" type="submit" disabled={saving}>{t('owner.saveAircraft')}</button>
          </form>

          <div className="owner-documents">
            {contractDownloadAvailable && (
              <div className="owner-contract-panel">
                <div>
                  <h3>{t('owner.contractTitle')}</h3>
                  <p>{t('owner.contractIntro')}</p>
                </div>
                <button className="btn btn-primary" type="button" onClick={downloadContract} disabled={saving}>
                  {t('owner.downloadContract')}
                </button>
              </div>
            )}
            <h3>{t('owner.documentsTitle')}</h3>
            <form className="owner-upload" onSubmit={uploadDocuments}>
              <label>
                <span>{t('owner.documentType')}</span>
                <select value={documentType} onChange={(e) => setDocumentType(e.target.value)}>
                  <option value="insurance">{t('owner.docInsurance')}</option>
                  <option value="registration">{t('owner.docRegistration')}</option>
                  <option value="photo">{t('owner.docPhoto')}</option>
                  <option value="supporting">{t('owner.docSupporting')}</option>
                </select>
              </label>
              <label>
                <span>{t('owner.files')}</span>
                <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFiles(e.target.files)} />
              </label>
              <button className="btn btn-secondary" type="submit" disabled={saving || !files || files.length === 0}>{t('owner.upload')}</button>
            </form>
            <ul className="owner-document-list">
              {(data?.documents || []).map((doc) => (
                <li key={doc.id}>
                  <strong>{doc.document_type}</strong>
                  <span>{doc.original_name || doc.file_name}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
};

export default OwnerPortalPage;
